import { isDevPreviewChannelOrigin } from 'common/config/preview-channel';

/**
 * Browser sign-in for a Pop-preview PR desktop build. The desktop
 * opens `/open-desktop-app?app=pop-preview` in the system browser, whose user agent never says
 * Pop-preview, so the hint rides the URL and is kept in localStorage: signing in first bounces
 * through /signin and Google/Okta, which come back to /open-desktop-app/<token> without the query.
 * Only an exact 'pop-preview' on a dev web origin changes anything; without it every env builds the
 * same `<protocol>://login/…` link as always.
 */
export const desktopAppHintParam = 'app';
export const previewDesktopAppHint = 'pop-preview';

const storageKey = 'popDesktopAppHint';
// Long enough for a Google/Okta round-trip, short enough that an abandoned attempt can't redirect a
// later Pop-dev sign-in.
const hintTtlMs = 30 * 60_000;
const devWebOrigin = 'https://dev.pop.com';

type StoredHint = { app: string; ts: number };

/**
 * What a full page load of `pathname` + `search` does to the stored hint: a fresh
 * `/open-desktop-app` (no login token, i.e. just opened by a desktop app) sets it when it carries
 * `?app=pop-preview` and clears it otherwise; every other page leaves it alone.
 */
export const nextDesktopAppHint = ({
  pathname,
  search,
  now,
}: {
  pathname: string;
  search: string;
  now: number;
}): StoredHint | 'clear' | 'keep' => {
  if (pathname.replace(/\/$/, '') !== '/open-desktop-app') return 'keep';
  return new URLSearchParams(search).get(desktopAppHintParam) === previewDesktopAppHint
    ? { app: previewDesktopAppHint, ts: now }
    : 'clear';
};

/** The desktop protocol a browser sign-in link targets. */
export const resolveDesktopLoginProtocol = ({
  defaultProtocol,
  storedHint,
  origin,
  now,
}: {
  defaultProtocol: string;
  storedHint: string | null;
  origin: string;
  now: number;
}) => {
  let hint: Partial<StoredHint> | undefined;
  try {
    hint = storedHint ? (JSON.parse(storedHint) as Partial<StoredHint>) : undefined;
  } catch {
    hint = undefined;
  }
  const isFresh = typeof hint?.ts === 'number' && now - hint.ts >= 0 && now - hint.ts < hintTtlMs;
  const isDevOrigin = origin === devWebOrigin || isDevPreviewChannelOrigin(origin);
  return hint?.app === previewDesktopAppHint && isFresh && isDevOrigin
    ? previewDesktopAppHint
    : defaultProtocol;
};

const readStoredHint = () => {
  try {
    return localStorage.getItem(storageKey);
  } catch {
    return null;
  }
};

/** Called once per page load of the web app (router.tsx). */
export const captureDesktopAppHint = (location: Pick<Location, 'pathname' | 'search'>) => {
  const next = nextDesktopAppHint({ pathname: location.pathname, search: location.search, now: Date.now() });
  try {
    if (next === 'clear') localStorage.removeItem(storageKey);
    else if (next !== 'keep') localStorage.setItem(storageKey, JSON.stringify(next));
  } catch {
    // No storage: the sign-in link keeps the default protocol; the manual login code still works.
  }
};

export const getDesktopLoginProtocol = (defaultProtocol: string) =>
  resolveDesktopLoginProtocol({
    defaultProtocol,
    storedHint: readStoredHint(),
    origin: window.location.origin,
    now: Date.now(),
  });

/** The hint is single-use: cleared once a login link has been handed to the desktop. */
export const clearDesktopAppHint = () => {
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // Nothing stored.
  }
};
